import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host {
      display: block;
      width: var(--uwc-fileupload-width, 100%);
      font-family: inherit;
    }

    /* ── Outer container — visual tokens match uwc-panel ────────────────────── */

    .uwc-fileupload {
      display:        flex;
      flex-direction: column;
      border:         1px solid var(--uwc-fileupload-border, ${t.borderControl});
      border-radius:  var(--uwc-fileupload-radius, ${t.radiusLg});
      overflow:       hidden;
      background:     var(--uwc-fileupload-bg, ${t.surface});
    }

    /* ── Toolbar ─────────────────────────────────────────────────────────────── */

    .uwc-fileupload__toolbar {
      display:       flex;
      align-items:   center;
      gap:           ${t.space2};
      padding:       var(--uwc-fileupload-toolbar-padding, ${t.space2} ${t.space3});
      border-bottom: 1px solid var(--uwc-fileupload-border, ${t.borderControl});
      flex-wrap:     wrap;
    }

    .uwc-fileupload__toolbar-actions {
      display:     flex;
      align-items: center;
      gap:         ${t.space2};
      margin-left: auto;
    }

    /* ── Content / drop zone ─────────────────────────────────────────────────── */

    .uwc-fileupload__content {
      flex:       1;
      position:   relative;
      padding:    ${t.space4} ${t.space5};
      transition: background ${t.durationBase};
    }

    .uwc-fileupload--dragover .uwc-fileupload__content {
      background:     color-mix(in oklab, ${t.primary} 5%, ${t.surface});
      outline:        2px dashed ${t.primary};
      outline-offset: -4px;
      border-radius:  ${t.radiusMd};
    }

    /* ── Empty / idle state ──────────────────────────────────────────────────── */

    .uwc-fileupload__empty {
      display:         flex;
      flex-direction:  column;
      align-items:     center;
      justify-content: center;
      gap:             ${t.space3};
      min-height:      var(--uwc-fileupload-height, 160px);
      color:           ${t.textMuted};
      text-align:      center;
      font-size:       ${t.fontSizeMd};
    }

    .uwc-fileupload__empty-icon {
      color: var(--uwc-fileupload-icon-color, ${t.primary});
      opacity: 0.65;
    }

    .uwc-fileupload--dragover .uwc-fileupload__empty-icon {
      opacity: 1;
    }

    /* ── Validation messages ─────────────────────────────────────────────────── */

    .uwc-fileupload__messages {
      display:        flex;
      flex-direction: column;
      gap:            ${t.space2};
      margin-bottom:  ${t.space3};
    }

    /* ── Progress bar (global, shown during XHR upload) ─────────────────────── */

    .uwc-fileupload__progress-wrap {
      display:        flex;
      flex-direction: column;
      gap:            4px;
      margin-bottom:  ${t.space3};
    }

    .uwc-fileupload__progress-header {
      display:         flex;
      justify-content: space-between;
      align-items:     center;
      font-size:       ${t.fontSizeXs};
      color:           ${t.textSecondary};
    }

    /* CSS-custom-property-driven progress bar.
       Set --uwc-progress-value on the element to control the fill width. */
    .uwc-progress {
      --uwc-progress-value: 0%;
      width:         100%;
      height:        var(--uwc-fileupload-progress-height, 4px);
      background:    var(--uwc-fileupload-progress-track, ${t.borderControl});
      border-radius: ${t.radiusFull};
      overflow:      hidden;
    }

    .uwc-progress::after {
      content:       '';
      display:       block;
      height:        100%;
      width:         var(--uwc-progress-value);
      background:    var(--uwc-fileupload-progress-color, ${t.primary});
      border-radius: ${t.radiusFull};
      transition:    width 250ms ${t.easingOut};
    }

    /* ── File list ───────────────────────────────────────────────────────────── */

    .uwc-fileupload__files {
      display:        flex;
      flex-direction: column;
    }

    .uwc-fileupload__file-row {
      display:     flex;
      align-items: center;
      gap:         ${t.space3};
      padding:     ${t.space3} 0;
    }

    .uwc-fileupload__file-row + .uwc-fileupload__file-row {
      border-top: 1px solid ${t.border};
    }

    /* ── Thumbnail ───────────────────────────────────────────────────────────── */

    .uwc-fileupload__thumbnail {
      flex-shrink:     0;
      border-radius:   ${t.radiusSm};
      overflow:        hidden;
      background:      ${t.surfaceRaised};
      color:           ${t.textSecondary};
      display:         flex;
      align-items:     center;
      justify-content: center;
    }

    .uwc-fileupload__thumbnail-img {
      width:      100%;
      height:     100%;
      object-fit: cover;
      display:    block;
    }

    /* ── File info ───────────────────────────────────────────────────────────── */

    .uwc-fileupload__file-info {
      flex:           1;
      min-width:      0;
      display:        flex;
      flex-direction: column;
      gap:            2px;
    }

    .uwc-fileupload__filename {
      font-size:     ${t.fontSizeMd};
      font-weight:   ${t.fontWeightMedium};
      color:         ${t.text};
      overflow:      hidden;
      text-overflow: ellipsis;
      white-space:   nowrap;
    }

    .uwc-fileupload__filesize {
      font-size: ${t.fontSizeXs};
      color:     ${t.textMuted};
    }

    /* ── Remove (×) button — delegated to uwc-button[text][icon-only] ──────── */

    /* Tint the remove icon on hover via the host's CSS custom property cascade */
    .uwc-fileupload__file-row uwc-button[part="remove-button"]:not([disabled]):hover {
      --uwc-btn-color: ${t.danger};
    }

    /* ── Hidden native file input ─────────────────────────────────────────────── */

    .uwc-fileupload__input {
      position:    absolute;
      width:       1px;
      height:      1px;
      overflow:    hidden;
      clip:        rect(0 0 0 0);
      white-space: nowrap;
    }

    /* ── Basic mode ──────────────────────────────────────────────────────────── */

    .uwc-fileupload--basic {
      display:       flex;
      align-items:   center;
      flex-direction: row;
      gap:           ${t.space2};
      border:        none;
      background:    transparent;
      border-radius: 0;
      box-shadow:    none;
      overflow:      visible;
    }

    .uwc-fileupload__basic-filename {
      display:       inline-flex;
      align-items:   center;
      gap:           ${t.space1};
      font-size:     ${t.fontSizeSm};
      color:         ${t.textSecondary};
      overflow:      hidden;
      text-overflow: ellipsis;
      white-space:   nowrap;
      max-width:     240px;
    }

    /* ── Disabled state ──────────────────────────────────────────────────────── */

    .uwc-fileupload--disabled {
      opacity:        0.65;
      pointer-events: none;
      user-select:    none;
    }

    /* ── Reduced motion ──────────────────────────────────────────────────────── */

    @media (prefers-reduced-motion: reduce) {
      .uwc-fileupload__content,
      .uwc-progress::after,
      .uwc-fileupload__msg-close {
        transition: none;
      }
    }
  `,
];
